import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation } from '@apollo/client'
import { MY_STUDENTS_QUERY, ASSIGN_LESSON_MUTATION } from '../query/gqlQueries.js'
import languages from '../languages'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// Assign one of MY saved lessons to specific students as homework.
//   <AssignLessonDialog meeting={{ id, title }} onClose={…} onAssigned={…} />
//
// You can only pick from students who have added you with your teaching code.
// There is deliberately no way to search all usernames: a teacher should never
// be able to read the names of people who are not their own students.
export default function AssignLessonDialog({ meeting, onClose, onAssigned }) {
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState([])
  const [due, setDue] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const { data, loading: loadingStudents } = useQuery(MY_STUDENTS_QUERY, { variables: {}, skip: !meeting, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [assign, { loading }] = useMutation(ASSIGN_LESSON_MUTATION)
  if (!meeting) return null

  // A lesson in a language goes to the students who learn that language with
  // you (they choose under Homework → My teachers); one without a language, to anyone.
  const all = data?.myStudents || []
  const lang = meeting.lang || null
  const students = all.filter(s => !lang || (s.langs || []).includes(lang)).map(s => s.username)
  const langName = lang ? (languages[lang] || lang) : null
  const needle = q.trim().toLowerCase()
  const results = students.filter(u => !picked.includes(u) && (!needle || u.toLowerCase().includes(needle)))
  const add = (u) => { setPicked(p => (p.includes(u) ? p : [...p, u])); setQ(''); setMsg('') }
  const remove = (u) => setPicked(p => p.filter(x => x !== u))
  const doAssign = async () => {
    setErr(''); setMsg('')
    if (picked.length === 0) { setErr('Pick at least one student.'); return }
    try {
      const r = await assign({ variables: { meetingId: meeting.id, students: picked, due: due || null } })
      const n = r.data?.assignLesson ?? 0
      setMsg(`Assigned to ${picked.length} student${picked.length === 1 ? '' : 's'}${n < picked.length ? ' (some were already assigned — due date updated)' : ''}.`)
      if (onAssigned) onAssigned()
    } catch (e) { setErr(e.message) }
  }

  const input = { width: '100%', boxSizing: 'border-box', background: 'var(--frost-field)', border: '1px solid var(--frost-border)', borderRadius: 10, color: 'var(--frost-text)', padding: '0.6rem 0.8rem', fontSize: '0.95rem', outline: 'none' }
  const chip = { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'var(--frost-right-tint)', border: '1px solid var(--frost-right-border)', color: 'var(--frost-right)', borderRadius: 999, padding: '0.2rem 0.65rem', fontSize: '0.85rem', cursor: 'pointer' }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Homework</div>
            <h2 className={styles.title}>Assign this lesson</h2>
          </div>
        </div>
        <div className={styles.container}>
          <p style={{ color: 'var(--frost-muted)', margin: '0 0 0.9rem' }}>
            <strong style={{ color: 'var(--frost-text)' }}>{meeting.title}</strong> — students will see it under <em>Homework</em>, do the lesson, and record their speaking answers for you to review.
          </p>

          <label style={{ display: 'block', color: 'var(--frost-muted)', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Students{langName ? ` · ${langName}` : ''}</label>
          {picked.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }}>
              {picked.map(u => <span key={u} style={chip} onClick={() => remove(u)} title="Remove">{u} ×</span>)}
            </div>
          )}
          {students.length > 6 && (
            <input style={{ ...input, marginBottom: '0.35rem' }} value={q} onChange={e => setQ(e.target.value)} placeholder="Filter your students…" autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && results[0]) add(results[0]) }} />
          )}
          <div style={{ background: 'var(--frost-field)', border: '1px solid var(--frost-border)', borderRadius: 10, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
            {loadingStudents && students.length === 0
              ? <div style={{ padding: '0.5rem 0.8rem', color: 'var(--frost-muted)', fontSize: '0.85rem' }}>Loading…</div>
              : students.length === 0 && all.length > 0
                ? <div style={{ padding: '0.6rem 0.8rem', color: 'var(--frost-muted)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    None of your students learn {langName} with you. A student chooses which languages you teach them under <strong style={{ color: 'var(--frost-text)' }}>Homework → My teachers</strong>.
                  </div>
              : students.length === 0
                ? <div style={{ padding: '0.6rem 0.8rem', color: 'var(--frost-muted)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    You have no students yet. Open <strong style={{ color: 'var(--frost-text)' }}>Homework → My students</strong> to get your teaching code, and send it to them — once they enter it, they will appear here.
                  </div>
                : results.length === 0
                  ? <div style={{ padding: '0.5rem 0.8rem', color: 'var(--frost-muted)', fontSize: '0.85rem' }}>{picked.length === students.length ? 'All of your students are picked.' : 'No match.'}</div>
                  : results.map(u => <div key={u} onClick={() => add(u)} style={{ padding: '0.5rem 0.8rem', cursor: 'pointer', borderTop: '1px solid var(--frost-border)', color: 'var(--frost-text)' }}>{u}</div>)}
          </div>

          <label style={{ display: 'block', color: 'var(--frost-muted)', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0.9rem 0 0.35rem' }}>Due (optional)</label>
          <input type="date" style={{ ...input, width: 'auto', colorScheme: 'inherit' }} value={due} onChange={e => setDue(e.target.value)} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '1.1rem', flexWrap: 'wrap' }}>
            <button onClick={doAssign} disabled={loading || picked.length === 0}
              style={{ background: 'var(--frost-action)', color: 'var(--frost-on-action)', border: 'none', borderRadius: 10, padding: '0.6rem 1.3rem', fontWeight: 700, cursor: 'pointer', opacity: picked.length === 0 ? 0.5 : 1 }}>
              {loading ? 'Assigning…' : `Assign to ${picked.length || '…'}`}
            </button>
            {msg && <span style={{ color: 'var(--frost-right)', fontSize: '0.9rem' }}>{msg}</span>}
            {err && <span style={{ color: 'var(--frost-wrong)', fontSize: '0.9rem' }}>{err}</span>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
